import { useState, useEffect } from "react";
import { 
  FaTimes, 
  FaUserCircle,
  FaMapMarkerAlt,
  FaMobileAlt,
  FaLaptop,
  FaTabletAlt,
  FaGlobe,
  FaHistory,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaIdCard,
  FaNetworkWired
} from "react-icons/fa";
import { MdOutlineTimer } from "react-icons/md";
import { UserJourney } from "../services/menuClick_service";

// Import du CSS séparé
import '../css/UserJourneyModal.css';

// Import Leaflet
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Correction des icônes Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

interface UserJourneyModalProps {
  user: UserJourney;
  onClose: () => void;
}

// Helper pour formater la date
const formatDateTime = (d: string) => 
  new Date(d).toLocaleString("fr-FR", { 
    day: "2-digit", 
    month: "short", 
    hour: "2-digit", 
    minute: "2-digit"
  });

const formatTime = (d: string) => 
  new Date(d).toLocaleTimeString("fr-FR", { 
    hour: "2-digit", 
    minute: "2-digit",
    second: "2-digit"
  });

const formatDuration = (ms: number): string => {
  if (!ms || ms < 1000) return '< 1s';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}min`;
  } else if (minutes > 0) {
    return `${minutes}min ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const getDeviceIcon = (device: string = 'desktop') => {
  switch(device) {
    case 'mobile': return <FaMobileAlt />;
    case 'tablet': return <FaTabletAlt />;
    default: return <FaLaptop />;
  }
};

// Fonction pour construire l'URL de l'image
const getProfileImageUrl = (path: string | null | undefined) => {
  if (!path) return null;
  
  try {
    let cleaned = path.replace(/^"+|"+$/g, '');
    cleaned = cleaned.replace(/\\/g, '/');
    cleaned = cleaned.replace(/\/+/g, '/');
    
    if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
      return cleaned;
    }
    
    const base = process.env.REACT_APP_API_BASE?.replace(/\/$/, '') || 'http://localhost:5200';
    
    return `${base}/${cleaned}`;
    
  } catch (error) {
    console.error("Erreur construction URL image:", error);
    return null;
  }
};

export default function UserJourneyModal({ user, onClose }: UserJourneyModalProps) {
  const [activeTab, setActiveTab] = useState<'timeline' | 'lastLogin'>('timeline');
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [selectedTimeRange, setSelectedTimeRange] = useState<{start: number, end: number} | null>(null);
  const [imageError, setImageError] = useState(false);

  const shouldShowIp = (email: string | undefined): boolean => {
    if (!email) return true;
    return !email.includes('site.terrier');
  };

  // Enrichir les données avec les sessions
  const sessions = user.sessions.map(session => {
    const sessionClicks = user.journey.filter(j => j.session_id === session.session_id);
    const start = new Date(session.start).getTime();
    const end = session.end ? new Date(session.end).getTime() : Date.now();
    
    return {
      ...session,
      duration: end - start,
      clicks: sessionClicks.map(click => ({
        menu_label: click.menu_label,
        clicked_at: click.clicked_at,
        device_type: click.device_type,
        location: click.location
      }))
    };
  }).sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());

  // Extraire les locations pour la carte
  const locations = user.journey
    .filter(j => j.location && j.location.latitude && j.location.longitude)
    .map(j => ({
      ...j.location,
      time: j.clicked_at,
      menu: j.menu_label,
      session_id: j.session_id
    }));

  // Timeline data pour la frise
  const timelineEvents = user.journey.map((j, index) => ({
    time: new Date(j.clicked_at).getTime(),
    label: j.menu_label,
    type: j.menu_key === 'login' ? 'login' : 
          j.menu_key === 'logout' ? 'logout' : 'action',
    index
  }));

  const minTime = Math.min(...timelineEvents.map(e => e.time));
  const maxTime = Math.max(...timelineEvents.map(e => e.time));
  const timeRange = maxTime - minTime;

  // Gérer l'expansion des sessions
  const toggleSession = (sessionId: string) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  // Filtrer les événements par plage de temps sélectionnée
  const filteredJourney = selectedTimeRange 
    ? user.journey.filter(j => {
        const time = new Date(j.clicked_at).getTime();
        return time >= selectedTimeRange.start && time <= selectedTimeRange.end;
      })
    : user.journey;

  const profileImageUrl = getProfileImageUrl(user.user.profile_picture);

  return (
    <div className="user-journey-modal">
      {/* Header avec infos techniques */}
      <div className="user-journey-header">
        <div className="user-info">
          <div className="user-avatar">
            {profileImageUrl && !imageError ? (
              <img 
                src={profileImageUrl}
                alt={user.user.fullName}
                onError={() => setImageError(true)}
              />
            ) : (
              <FaUserCircle />
            )}
          </div>
          <div className="user-details">
            <h2>{user.user.fullName}</h2>
            <div className="user-meta">
              <span className="user-meta-item">
                <FaIdCard size={12} /> ID: {user.user.id}
              </span>
              
              {/* IP masquée pour les emails contenant "mialy.softimad" */}
              {shouldShowIp(user.user.email) && (
                <span className="user-meta-item">
                  <FaNetworkWired size={12} /> IP: {user.user.last_login_ip || 'Non disponible'}
                </span>
              )}
              
              {/* Dernière localisation connue */}
              {user.user.last_login_location && (
                <span className="user-meta-item location-badge">
                  <FaMapMarkerAlt size={12} color="#4bb0c0" />
                  {user.user.last_login_location}
                </span>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="close-button">
          <FaTimes />
        </button>
      </div>

      {/* Stats rapides */}
      <div className="stats-bar">
        <div className="stat-item">
          <FaClock color="#4bb0c0" />
          <span><strong>{user.stats.total_sessions}</strong> sessions</span>
        </div>
        <div className="stat-item">
          <FaHistory color="#4bb0c0" />
          <span><strong>{user.stats.total_clicks}</strong> actions</span>
        </div>
        {user.user.last_login_at && (
          <div className="stat-item" title="Localisation">
            <MdOutlineTimer color="#4bb0c0" />
            <span>{formatDateTime(user.user.last_login_at)}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="user-journey-tabs">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`user-journey-tab ${activeTab === 'timeline' ? 'active' : ''}`}
        >
          <FaHistory /> Historique
        </button>
        <button
          onClick={() => setActiveTab('lastLogin')}
          className={`user-journey-tab ${activeTab === 'lastLogin' ? 'active' : ''}`}
        >
          <FaGlobe /> Dernière connexion
        </button>
      </div>

      {/* Content */}
      <div className="user-journey-content">
        {activeTab === 'timeline' ? (
          <div className="timeline-sessions">
            {sessions.map((session, index) => {
              const isExpanded = expandedSessions.has(session.session_id);
              
              return (
                <div key={session.session_id} className="timeline-session-card">
                  {/* En-tête de session */}
                  <div 
                    className="timeline-session-header"
                    onClick={() => toggleSession(session.session_id)}
                  >
                    <div className="timeline-session-header-left">
                      <span className="timeline-session-number">
                        Session #{sessions.length - index}
                      </span>
                      <span className="timeline-session-time">
                        {formatDateTime(session.start)}
                        {session.end && ` - ${formatDateTime(session.end)}`}
                      </span>
                    </div>
                    <div className="timeline-session-header-right">
                      <span className="timeline-session-duration">
                        {formatDuration(session.duration)}
                      </span>
                      <span className="timeline-session-count">
                        {session.clicks.length} actions
                      </span>
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  {/* Détails de session (expandable) */}
                  {isExpanded && (
                    <div className="timeline-session-details">
                      <div className="timeline-session-meta">
                        {session.clicks[0]?.device_type && (
                          <span className="timeline-meta-tag">
                            {getDeviceIcon(session.clicks[0].device_type)}
                            {session.clicks[0].device_type}
                          </span>
                        )}
                        {session.clicks[0]?.location && (
                          <span className="timeline-meta-tag">
                            <FaMapMarkerAlt />
                            {session.clicks[0].location.city}, {session.clicks[0].location.country}
                          </span>
                        )}
                      </div>

                      <div className="timeline-actions-list">
                        {session.clicks.map((click, clickIndex) => (
                          <div key={clickIndex} className="timeline-action-item">
                            <div className="timeline-action-time">
                              {formatTime(click.clicked_at)}
                            </div>
                            <div className="timeline-action-content">
                              <span className="timeline-action-label">
                                {click.menu_label}
                              </span>
                              {click.location && (
                                <span className="timeline-action-location">
                                  <FaMapMarkerAlt size={10} />
                                  {click.location.city}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <LastLoginView user={user} shouldShowIp={shouldShowIp} />
        )}
      </div>

      {/* Bouton pour réinitialiser le filtre */}
      {selectedTimeRange && (
        <div className="filter-badge">
          <span>Filtre: {formatDateTime(new Date(selectedTimeRange.start).toISOString())}</span>
          <button onClick={() => setSelectedTimeRange(null)} className="filter-clear">
            <FaTimes size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function LastLoginView({ user, shouldShowIp }: { user: UserJourney; shouldShowIp: (email: string | undefined) => boolean }) {
  const lastLogin = {
    ip: user.user.last_login_ip,
    location: user.user.last_login_location,
    country: user.user.last_login_country,
    city: user.user.last_login_city,
    latitude: user.user.last_login_latitude ? parseFloat(user.user.last_login_latitude as any) : null,
    longitude: user.user.last_login_longitude ? parseFloat(user.user.last_login_longitude as any) : null,
    userAgent: user.user.last_login_user_agent,
    time: user.user.last_login_at
  };

  if (!lastLogin.time) {
    return (
      <div className="map-empty">
        <FaMapMarkerAlt size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
        <p>Aucune donnée de dernière connexion disponible</p>
      </div>
    );
  }

  const formatUserAgent = (ua: string | null | undefined): string => {
    if (!ua) return 'Inconnu';
    
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Google Chrome';
    if (ua.includes('Firefox')) return 'Mozilla Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edg')) return 'Microsoft Edge';
    if (ua.includes('MSIE') || ua.includes('Trident')) return 'Internet Explorer';
    
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('iPhone')) return 'iOS (iPhone)';
    if (ua.includes('iPad')) return 'iOS (iPad)';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('Linux')) return 'Linux';
    
    return ua.substring(0, 50) + '...';
  };

  // Fonction pour formater les coordonnées en toute sécurité
  const formatCoordinate = (coord: number | null): string => {
    if (coord === null || coord === undefined || isNaN(coord)) return 'Non disponible';
    return coord.toFixed(4);
  };

  // Vérifier si les coordonnées sont valides
  const hasValidCoordinates = 
    lastLogin.latitude !== null && 
    lastLogin.longitude !== null && 
    !isNaN(lastLogin.latitude) && 
    !isNaN(lastLogin.longitude);

  return (
    <div className="last-login-container">
      {/* Carte pour la dernière connexion si coordonnées disponibles */}
      {hasValidCoordinates ? (
        <div className="last-login-map">
          <MapContainer
            center={[lastLogin.latitude!, lastLogin.longitude!]}
            zoom={10}
            style={{ height: '300px', width: '100%', borderRadius: '12px' }}
            scrollWheelZoom={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[lastLogin.latitude!, lastLogin.longitude!]}>
              <Popup>
                <div style={{ minWidth: 150 }}>
                  <strong>Dernière connexion</strong>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {lastLogin.city || 'Ville inconnue'}, {lastLogin.country || 'Pays inconnu'}
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>
                    {formatDateTime(lastLogin.time)}
                  </div>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      ) : (
        <div className="map-empty" style={{ height: '200px' }}>
          <FaMapMarkerAlt size={32} style={{ opacity: 0.3 }} />
          <p>Position géographique non disponible</p>
        </div>
      )}

      {/* Détails de la dernière connexion */}
      <div className="last-login-details">
        <h3>
          <FaClock style={{ marginRight: 8 }} />
          Dernière connexion : {formatDateTime(lastLogin.time)}
        </h3>
        
        <div className="last-login-grid">
          <div className="last-login-card">
            <div className="last-login-card-header">
              <FaMapMarkerAlt color="#4bb0c0" />
              Localisation
            </div>
            <div className="last-login-card-content">
              <p>
                <strong>Ville :</strong> {lastLogin.city || 'Non disponible'}
              </p>
              <p>
                <strong>Pays :</strong> {lastLogin.country || 'Non disponible'}
              </p>
              
              {/* IP masquée pour les emails contenant "mialy.softimad" */}
              {shouldShowIp(user.user.email) && (
                <p>
                  <strong>Adresse IP :</strong> {lastLogin.ip || 'Non disponible'}
                </p>
              )}
              
              {hasValidCoordinates && (
                <p>
                  <strong>Coordonnées :</strong> {formatCoordinate(lastLogin.latitude)}, {formatCoordinate(lastLogin.longitude)}
                </p>
              )}
            </div>
          </div>

          <div className="last-login-card">
            <div className="last-login-card-header">
              <FaLaptop color="#4bb0c0" />
              Appareil
            </div>
            <div className="last-login-card-content">
              <p>
                <strong>Navigateur :</strong> {formatUserAgent(lastLogin.userAgent)}
              </p>
              <p>
                <strong>User Agent :</strong>
                <span 
                  className="user-agent-full" 
                  title={lastLogin.userAgent || undefined}
                >
                  {lastLogin.userAgent ? lastLogin.userAgent.substring(0, 80) + '...' : 'Non disponible'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}